import React, { useState } from 'react';
import { Button } from './ui/button';
import { 
  Brain, 
  Camera, 
  Utensils, 
  Footprints, 
  Target, 
  Watch,
  ChevronRight,
  Star,
  Check,
  Sparkles,
  Zap,
  TrendingUp,
  Heart,
  Shield,
  Play,
  ArrowRight,
  Calculator,
  X
} from 'lucide-react';

const LandingPage = ({ onGetStarted, onLogin, onCalculators }) => {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showDemo, setShowDemo] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  // App screenshots for carousel
  const screenshots = [
    { src: '/screenshots/mainpage.jpg', alt: 'NutriSteps Home - Feature overview with Nutrition, Walking, AI Planner', label: 'Home' },
    { src: '/screenshots/nutrition.jpg', alt: 'Nutrition Tracker - Calorie rings, macros tracking, daily allowance', label: 'Nutrition' },
    { src: '/screenshots/walking.jpg', alt: 'Walking Timer - Step tracking, calorie estimation, workout configuration', label: 'Walking' },
    { src: '/screenshots/ai_planner.jpg', alt: 'AI Planner - Diet and walking plans powered by Claude AI', label: 'AI Planner' },
    { src: '/screenshots/statistics.jpg', alt: 'Statistics Dashboard - Weekly and monthly analytics charts', label: 'Statistics' }
  ];

  // Auto-advance carousel
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % screenshots.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [screenshots.length]);

  const features = [
    {
      icon: Camera,
      title: "AI Calorie Counter from Photo",
      description: "Snap a photo of your meal and get instant nutrition breakdown. Our AI recognizes food and calculates calories automatically.",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: Utensils,
      title: "AI Calorie Counter from Menu",
      description: "Scan any restaurant menu and get calorie estimates for every dish. Perfect for eating out while staying on track.",
      gradient: "from-amber-500 to-orange-500"
    },
    {
      icon: Brain,
      title: "AI Walking & Nutrition Planner",
      description: "Get personalized walking programs and nutrition plans created by AI based on your goals, body data, and preferences.",
      gradient: "from-violet-500 to-purple-500"
    },
    {
      icon: Target,
      title: "Complete Tracker with Nutrition",
      description: "Track macros and calories. Set daily goals and monitor your progress with detailed analytics.",
      gradient: "from-green-500 to-emerald-500"
    },
    {
      icon: Footprints,
      title: "Walking Programs",
      description: "AI-generated walking programs tailored to your fitness level. Track steps, distance, and calories burned.",
      gradient: "from-pink-500 to-rose-500"
    },
    {
      icon: Watch,
      title: "Wearable Sync",
      description: "Connect Fitbit, Polar, and Withings. Your steps and health data synced automatically in one place.",
      gradient: "from-indigo-500 to-blue-500"
    }
  ];

  const stats = [];

  const testimonials = [];

  const faqs = [
    {
      question: "What makes NutriSteps the best all in one fitness app?",
      answer: "NutriSteps combines AI-powered nutrition tracking, walking planning, and health monitoring in one seamless app. Our fitness app with AI coach provides personalized guidance, while features like AI calorie counter from photo and AI calorie counter from menu make tracking effortless."
    },
    {
      question: "How does the AI calorie counter from photo work?",
      answer: "Simply take a photo of your meal, and our advanced AI analyzes the image to identify foods and estimate portions. Within seconds, you get a complete nutrition breakdown including calories, protein, carbs, and fats. It's the fastest way to track your meals."
    },
    {
      question: "Can the AI planner create custom programs?",
      answer: "Yes! Our AI planner considers your body data, goals, available time, and preferences to create personalized walking and nutrition plans. Whether you want structured walking programs or balanced meal plans, the AI adapts to your needs."
    },
    {
      question: "What wearables does the app support?",
      answer: "NutriSteps syncs with Fitbit, Polar, and Withings. Your steps and health data are automatically imported, making it a truly complete tracker with nutrition and fitness data in one place."
    },
    {
      question: "Is this nutrition app suitable for any fitness level?",
      answer: "Absolutely! Whether you're just starting your fitness journey or you're experienced, our nutrition app and fitness app with AI coach adapts to your level. The AI creates appropriate meal plans and walking programs for beginners to advanced users."
    },
    {
      question: "How accurate is the AI calorie counter from menu?",
      answer: "Our AI calorie counter from menu uses a vast database of restaurant dishes combined with AI image recognition. While estimates may vary slightly, most users find it accurate within 10-15%, making it perfect for maintaining awareness while dining out."
    }
  ];

  const howItWorks = [
    {
      step: "1",
      title: "Snap or Scan",
      description: "Take a photo of your food or scan a restaurant menu. Our AI instantly recognizes dishes and calculates nutrition.",
      icon: Camera
    },
    {
      step: "2", 
      title: "Get AI Guidance",
      description: "Receive personalized meal suggestions and AI-generated walking & nutrition plans tailored to your goals.",
      icon: Brain
    },
    {
      step: "3",
      title: "Track & Achieve",
      description: "Monitor your progress with our complete tracker. Watch your fitness improve day by day.",
      icon: TrendingUp
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950 border-none shadow-none">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <Footprints className="h-5 w-5 text-white" />
              </div>
              <span className="text-xl font-bold">NutriSteps</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button 
                variant="ghost" 
                onClick={onCalculators}
                className="text-slate-300 hover:text-white hidden sm:flex"
                data-testid="nav-calculators-btn"
              >
                <Calculator className="h-4 w-4 mr-2" />
                Calculators
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={onCalculators}
                className="text-slate-300 hover:text-white sm:hidden"
                data-testid="nav-calculators-btn-mobile"
              >
                <Calculator className="h-5 w-5" />
              </Button>
              <Button 
                variant="ghost" 
                onClick={onLogin}
                className="text-slate-300 hover:text-white"
              >
                Sign In
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/10 via-transparent to-transparent" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute top-40 right-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 mb-8">
              <Sparkles className="h-4 w-4 text-blue-400" />
              <span className="text-sm text-blue-400">All in One Fitness App with AI Coach</span>
            </div>
            
            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-white via-blue-100 to-cyan-100 bg-clip-text text-transparent">
                AI Calorie Counter from Photo
              </span>
              <br />
              <span className="text-slate-300">& Complete Nutrition Tracker</span>
            </h1>
            
            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
              The ultimate <strong className="text-white">fitness app with AI coach</strong>. 
              Snap meals, scan menus, plan your walking & nutrition. Your <strong className="text-white">complete tracker with nutrition</strong> and fitness in one app.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <Button 
                size="lg"
                onClick={onGetStarted}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-lg px-8 py-6 rounded-xl shadow-lg shadow-blue-500/25"
              >
                Start 14-Day Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => setShowDemo(true)}
                className="border-slate-700 text-slate-300 hover:bg-slate-800 text-lg px-8 py-6 rounded-xl"
                data-testid="watch-demo-btn"
              >
                <Play className="mr-2 h-5 w-5" />
                Watch Demo
              </Button>
            </div>
            
            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-500" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-500" />
                <span>Cancel anytime</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-500" />
                <span>GDPR compliant</span>
              </div>
            </div>
          </div>
          
          {/* Phone Mockup Carousel */}
          <div className="mt-16 flex justify-center">
            <div className="relative">
              {/* Phone Frame */}
              <div className="relative w-[280px] sm:w-[320px] mx-auto">
                {/* Outer glow effect */}
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/30 via-cyan-500/30 to-blue-500/30 rounded-[3rem] blur-2xl" />
                
                {/* Phone body */}
                <div className="relative bg-slate-900 rounded-[2.5rem] p-2 border-4 border-slate-700 shadow-2xl">
                  {/* Screen */}
                  <div className="relative rounded-[2rem] overflow-hidden bg-slate-950 aspect-[9/19.5]">
                    {/* Dynamic Island / Notch */}
                    <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full z-10" />
                    
                    {/* Screenshots */}
                    <div className="relative w-full h-full">
                      {screenshots.map((screenshot, index) => (
                        <img
                          key={index}
                          src={screenshot.src}
                          alt={screenshot.alt}
                          className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ${
                            index === currentSlide ? 'opacity-100' : 'opacity-0'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Carousel Indicators */}
              <div className="flex justify-center gap-2 mt-6">
                {screenshots.map((screenshot, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all text-xs ${
                      index === currentSlide 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                    }`}
                    data-testid={`carousel-dot-${index}`}
                  >
                    <span>{screenshot.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      {stats.length > 0 && (
      <section className="py-12 px-4 sm:px-6 lg:px-8 border-y border-slate-800 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="text-slate-400 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" id="features">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              All in One Fitness App Features
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Everything you need for nutrition tracking, walking planning, and health success. 
              Powered by AI coach technology.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-6 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-slate-700 transition-all hover:transform hover:scale-[1.02]"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4`}>
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-slate-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              How Our AI Nutrition App Works
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              From AI calorie counter from photo to AI walking & nutrition planner - 
              getting fit has never been easier.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((item, index) => (
              <div key={index} className="text-center">
                <div className="relative inline-block mb-6">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 flex items-center justify-center">
                    <item.icon className="h-8 w-8 text-blue-400" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-sm font-bold">
                    {item.step}
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-slate-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Features Showcase */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/30 mb-4">
                <Brain className="h-4 w-4 text-violet-400" />
                <span className="text-sm text-violet-400">Powered by AI</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Fitness App with AI Coach
              </h2>
              <p className="text-slate-400 text-lg mb-8">
                Our AI coach is like having a personal trainer and nutritionist in your pocket. 
                Get personalized advice, meal suggestions, and plan adjustments based on your progress.
              </p>
              
              <div className="space-y-4">
                {[
                  "AI Calorie Counter from Photo - instant meal analysis",
                  "AI Calorie Counter from Menu - dine out with confidence", 
                  "AI Walking & Nutrition Planner - personalized plans",
                  "AI Health Coach - voice-powered health Q&A"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                      <Check className="h-4 w-4 text-green-500" />
                    </div>
                    <span className="text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={onGetStarted}
                className="mt-8 bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600"
              >
                Try AI Features Free
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-500/20 to-purple-500/20 rounded-3xl blur-3xl" />
              
              {/* Phone mockup with actual screenshots */}
              <div className="relative bg-slate-900 border border-slate-800 rounded-3xl p-4 sm:p-6">
                <div className="relative w-full max-w-[240px] mx-auto">
                  {/* Phone frame */}
                  <div className="bg-slate-800 rounded-[2rem] p-1.5 border-2 border-slate-700">
                    <div className="relative rounded-[1.5rem] overflow-hidden aspect-[9/19.5] bg-slate-950">
                      {/* Dynamic island */}
                      <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-16 h-4 bg-black rounded-full z-10" />
                      
                      {/* AI Planner screenshot (most relevant for AI section) */}
                      <img
                        src="/screenshots/ai_planner.jpg"
                        alt="AI Planner - Personalized diet and walking plans"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Feature badges below phone */}
                <div className="mt-4 space-y-2">
                  <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-xl">
                    <Camera className="h-6 w-6 text-blue-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-sm">Photo Analyzed</div>
                      <div className="text-xs text-slate-400 truncate">Grilled chicken salad</div>
                    </div>
                    <div className="ml-auto text-right">
                      <div className="text-green-400 font-bold text-sm">450 cal</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-xl">
                    <Zap className="h-6 w-6 text-violet-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-sm">Plan Generated</div>
                      <div className="text-xs text-slate-400 truncate">Walking + nutrition plan</div>
                    </div>
                    <span className="ml-auto px-2 py-0.5 bg-violet-500/20 text-violet-400 rounded text-xs">AI</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Loved by Fitness Enthusiasts
            </h2>
            <p className="text-slate-400 text-lg">
              See why thousands choose our all in one fitness app
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="p-6 rounded-2xl bg-slate-900/50 border border-slate-800"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-slate-300 mb-4">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold">{testimonial.name}</div>
                  <div className="text-sm text-slate-500">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Pricing */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" id="pricing">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-slate-400 text-lg">
              Start free, upgrade when you're ready
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Monthly Plan */}
            <div className="relative p-8 rounded-3xl bg-slate-800/50 border border-slate-700">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-2">Monthly</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-bold">€4.99</span>
                  <span className="text-slate-400">/month</span>
                </div>
                <p className="text-slate-400 mt-2">Flexible monthly billing</p>
              </div>
              
              <div className="space-y-3 mb-8">
                {[
                  "AI Calorie Counter from Photo",
                  "AI Calorie Counter from Menu", 
                  "AI Walking & Nutrition Planner",
                  "Complete Tracker with Nutrition",
                  "Unlimited Walking Plans",
                  "Wearable Sync",
                  "Progress Photos & Analytics",
                  "Priority Support"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={onGetStarted}
                variant="outline"
                className="w-full border-slate-600 hover:bg-slate-700 py-6 text-lg"
                data-testid="monthly-plan-btn"
              >
                Start Free Trial
              </Button>
              
              <p className="text-center text-sm text-slate-500 mt-4">
                14-day free trial included
              </p>
            </div>
            
            {/* Yearly Plan - Most Popular */}
            <div className="relative p-8 rounded-3xl bg-gradient-to-b from-blue-500/10 to-transparent border border-blue-500/30">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-blue-500 rounded-full text-sm font-medium">
                Most Popular - Save 33%
              </div>
              
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-2">Yearly</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-bold">€39.99</span>
                  <span className="text-slate-400">/year</span>
                </div>
                <p className="text-slate-400 mt-2">
                  <span className="line-through text-slate-500">€59.88</span>
                  <span className="text-green-400 ml-2">Save €19.89</span>
                </p>
              </div>
              
              <div className="space-y-3 mb-8">
                {[
                  "AI Calorie Counter from Photo",
                  "AI Calorie Counter from Menu", 
                  "AI Walking & Nutrition Planner",
                  "Complete Tracker with Nutrition",
                  "Unlimited Walking Plans",
                  "Wearable Sync (Fitbit, Polar, Withings)",
                  "Progress Photos & Analytics",
                  "Priority AI Coach Support"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={onGetStarted}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 py-6 text-lg"
                data-testid="yearly-plan-btn"
              >
                Start Free Trial
              </Button>
              
              <p className="text-center text-sm text-slate-500 mt-4">
                14-day free trial • No credit card required
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/30" id="faq">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-400 text-lg">
              Everything you need to know about our nutrition app
            </p>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="rounded-xl border border-slate-800 overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full p-5 text-left flex items-center justify-between bg-slate-900/50 hover:bg-slate-900 transition-colors"
                >
                  <span className="font-medium pr-4">{faq.question}</span>
                  <ChevronRight className={`h-5 w-5 text-slate-400 transition-transform flex-shrink-0 ${expandedFaq === index ? 'rotate-90' : ''}`} />
                </button>
                {expandedFaq === index && (
                  <div className="p-5 pt-0 bg-slate-900/50">
                    <p className="text-slate-400">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="p-12 rounded-3xl bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-blue-500/10 border border-blue-500/20">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Start Your Fitness Journey Today
            </h2>
            <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
              Join thousands using our all in one fitness app. AI calorie counter from photo, 
              AI walking & nutrition planner, and complete tracker with nutrition - all in one place.
            </p>
            <Button 
              size="lg"
              onClick={onGetStarted}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-lg px-8 py-6 rounded-xl shadow-lg shadow-blue-500/25"
            >
              Get Started Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <Footprints className="h-4 w-4 text-white" />
              </div>
              <span className="font-bold">NutriSteps</span>
            </div>
            
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <span>All in One Fitness App</span>
              <span>•</span>
              <span>AI Nutrition</span>
              <span>•</span>
              <span>Walking Tracker</span>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Shield className="h-4 w-4" />
              <span>GDPR Compliant</span>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-slate-800 text-center text-sm text-slate-500">
            <p>© 2025 NutriSteps. All rights reserved. The best fitness app with AI coach for any fitness level.</p>
          </div>
        </div>
      </footer>

      {/* Demo Modal */}
      {showDemo && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setShowDemo(false)}
        >
          <div 
            className="relative bg-slate-900 rounded-2xl border border-slate-700 max-w-lg w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div className="flex items-center gap-2">
                <Play className="h-5 w-5 text-cyan-400" />
                <h3 className="text-lg font-semibold text-white">App Demo</h3>
              </div>
              <button 
                onClick={() => setShowDemo(false)}
                className="p-1 rounded-lg hover:bg-slate-800 transition-colors"
                data-testid="close-demo-btn"
              >
                <X className="h-5 w-5 text-slate-400" />
              </button>
            </div>
            
            {/* Demo Content */}
            <div className="p-4">
              {/* Screenshot Carousel in Modal */}
              <div className="bg-slate-800 rounded-xl overflow-hidden mb-4">
                <div className="relative aspect-[9/16] max-h-[400px]">
                  {screenshots.map((screenshot, index) => (
                    <img
                      key={index}
                      src={screenshot.src}
                      alt={screenshot.alt}
                      className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ${
                        index === currentSlide ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                  ))}
                </div>
                
                {/* Modal Carousel Controls */}
                <div className="flex justify-center gap-2 p-3 bg-slate-900/50">
                  {screenshots.map((screenshot, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`px-2 py-1 rounded text-xs transition-all ${
                        index === currentSlide 
                          ? 'bg-cyan-500 text-white' 
                          : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                      }`}
                    >
                      {screenshot.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Feature Highlights */}
              <div className="space-y-3 mb-4">
                <h4 className="text-sm font-semibold text-slate-300">What you'll see:</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2 text-slate-400">
                    <Camera className="h-3 w-3 text-blue-400" />
                    <span>AI Photo Calorie Counter</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400">
                    <Utensils className="h-3 w-3 text-amber-400" />
                    <span>Menu Scanner</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400">
                    <Footprints className="h-3 w-3 text-green-400" />
                    <span>Walking Tracker</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400">
                    <Brain className="h-3 w-3 text-purple-400" />
                    <span>AI Fitness Planner</span>
                  </div>
                </div>
              </div>
              
              {/* CTA */}
              <Button 
                onClick={() => { setShowDemo(false); onGetStarted(); }}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                data-testid="demo-cta-btn"
              >
                Start Your Free Trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
